import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Avatar,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
  Modal,
  message,
  DatePicker,
  Badge,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  FileTextOutlined,
  CalendarOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { HRContract, HREmployee, HRContractType } from "../../types/hr";
import { useHRAPI } from "../../hooks/useHRAPI";
import ContractForm from "./ContractForm";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { confirm } = Modal;

const ContractList: React.FC = () => {
  const [contracts, setContracts] = useState<HRContract[]>([]);
  const [employees, setEmployees] = useState<HREmployee[]>([]);
  const [contractTypes, setContractTypes] = useState<HRContractType[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingContract, setEditingContract] = useState<HRContract | null>(
    null
  );
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [employeeFilter, setEmployeeFilter] = useState<string>("all");
  const [contractTypeFilter, setContractTypeFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null);

  const {
    getContracts,
    getEmployees,
    getContractTypes,
    deleteContract: deleteContractAPI,
    loading: apiLoading,
  } = useHRAPI();

  useEffect(() => {
    fetchContracts();
    fetchEmployees();
    fetchContractTypes();
  }, []);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const result = await getContracts();
      if (result) {
        setContracts(result);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des contrats:", error);
      message.error("Erreur lors du chargement des contrats");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const result = await getEmployees();
      if (result) {
        setEmployees(result);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des employés:", error);
    }
  };

  const fetchContractTypes = async () => {
    try {
      const result = await getContractTypes();
      if (result) {
        setContractTypes(result);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des types de contrats:", error);
    }
  };

  const handleAdd = () => {
    setEditingContract(null);
    setIsModalVisible(true);
  };

  const handleEdit = (contract: HRContract) => {
    setEditingContract(contract);
    setIsModalVisible(true);
  };

  const handleDelete = (contract: HRContract) => {
    confirm({
      title: "Êtes-vous sûr de vouloir supprimer ce contrat ?",
      content: `Contrat de ${contract.employee_first_name} ${contract.employee_last_name}`,
      icon: <ExclamationCircleOutlined />,
      okText: "Oui, supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const result = await deleteContractAPI(contract.id);
          if (result !== null) {
            message.success("Contrat supprimé avec succès");
            fetchContracts();
          }
        } catch (error) {
          console.error("Erreur lors de la suppression:", error);
          message.error("Erreur lors de la suppression du contrat");
        }
      },
    });
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    setEditingContract(null);
    fetchContracts();
  };

  const getStatusColor = (status: string) => {
    const colors = {
      draft: "default",
      active: "success",
      expired: "warning",
      terminated: "error",
      suspended: "processing",
    };
    return colors[status as keyof typeof colors] || "default";
  };

  const getStatusText = (status: string) => {
    const texts = {
      draft: "Brouillon",
      active: "Actif",
      expired: "Expiré",
      terminated: "Terminé",
      suspended: "Suspendu",
    };
    return texts[status as keyof typeof texts] || status;
  };

  const getSalaryTypeText = (type: string) => {
    const texts = {
      monthly: "Mensuel",
      daily: "Journalier",
      hourly: "Horaire",
    };
    return texts[type as keyof typeof texts] || type;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const calculateTotalSalary = (contract: HRContract) => {
    return contract.gross_salary;
  };

  const filteredContracts = contracts.filter((contract) => {
    const matchesSearch =
      !searchText ||
      contract.employee_first_name
        ?.toLowerCase()
        .includes(searchText.toLowerCase()) ||
      contract.employee_last_name
        ?.toLowerCase()
        .includes(searchText.toLowerCase()) ||
      contract.employee_number
        ?.toLowerCase()
        .includes(searchText.toLowerCase()) ||
      contract.contract_number
        ?.toLowerCase()
        .includes(searchText.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || contract.status === statusFilter;
    const matchesEmployee =
      employeeFilter === "all" ||
      contract.employee_id.toString() === employeeFilter;
    const matchesContractType =
      contractTypeFilter === "all" ||
      contract.contract_type_id.toString() === contractTypeFilter;

    let matchesDateRange = true;
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dayjs(contract.start_date);
      matchesDateRange =
        startDate.isAfter(dateRange[0]) && startDate.isBefore(dateRange[1]);
    }

    return (
      matchesSearch &&
      matchesStatus &&
      matchesEmployee &&
      matchesContractType &&
      matchesDateRange
    );
  });

  // Statistiques
  const stats = {
    total: contracts.length,
    active: contracts.filter((c) => c.status === "active").length,
    expiring: contracts.filter(
      (c) =>
        c.days_until_expiry &&
        c.days_until_expiry <= 30 &&
        c.days_until_expiry > 0
    ).length,
    totalSalaryBudget: contracts
      .filter((c) => c.status === "active")
      .reduce((sum, c) => sum + calculateTotalSalary(c), 0),
  };

  const columns: ColumnsType<HRContract> = [
    {
      title: "Employé",
      key: "employee",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            size="small"
            icon={<UserOutlined />}
            style={{ backgroundColor: "#1890ff" }}
          />
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.employee_first_name} {record.employee_last_name}
            </div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.employee_number}
            </Text>
          </div>
        </div>
      ),
      width: 200,
    },
    {
      title: "N° Contrat",
      dataIndex: "contract_number",
      key: "contract_number",
      render: (text) => text || "-",
      width: 120,
    },
    {
      title: "Type",
      dataIndex: "contract_type_name",
      key: "contract_type_name",
      width: 130,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
      width: 100,
    },
    {
      title: "Date début",
      dataIndex: "start_date",
      key: "start_date",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
      width: 110,
    },
    {
      title: "Date fin",
      dataIndex: "end_date",
      key: "end_date",
      render: (date) =>
        date ? dayjs(date).format("DD/MM/YYYY") : "Indéterminée",
      width: 110,
    },
    {
      title: "Salaire brut",
      key: "gross_salary",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {formatCurrency(calculateTotalSalary(record))}
          </div>
          <Text type="secondary" style={{ fontSize: "11px" }}>
            {getSalaryTypeText(record.salary_type)}
          </Text>
        </div>
      ),
      width: 130,
    },
    {
      title: "Expiration",
      key: "expiry",
      render: (_, record) => {
        if (!record.end_date) return "-";

        const daysRemaining = dayjs(record.end_date).diff(dayjs(), "day");

        if (daysRemaining < 0) {
          return <Tag color="error">Expiré</Tag>;
        } else if (daysRemaining <= 30) {
          return (
            <Badge count={daysRemaining} style={{ backgroundColor: "#ff4d4f" }}>
              <Tag color="warning">{daysRemaining}j restants</Tag>
            </Badge>
          );
        } else {
          return <Tag color="default">{daysRemaining}j restants</Tag>;
        }
      },
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir les détails">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
      width: 120,
      fixed: "right",
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      {/* En-tête avec statistiques */}
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div>
            <Title
              level={2}
              style={{
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <FileTextOutlined /> Gestion des Contrats
            </Title>
            <Text type="secondary">
              Gérez les contrats de travail de vos employés
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
          >
            Nouveau contrat
          </Button>
        </div>

        {/* Statistiques */}
        <Row gutter={16}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total contrats"
                value={stats.total}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Contrats actifs"
                value={stats.active}
                valueStyle={{ color: "#3f8600" }}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Expirent bientôt"
                value={stats.expiring}
                valueStyle={{
                  color: stats.expiring > 0 ? "#cf1322" : "#3f8600",
                }}
                prefix={<CalendarOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Budget salarial"
                value={stats.totalSalaryBudget}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: "#1890ff" }}
                prefix={<DollarOutlined />}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Filtres */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Input
              placeholder="Rechercher un contrat..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Statut"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: "100%" }}
            >
              <Option value="all">Tous les statuts</Option>
              <Option value="draft">Brouillon</Option>
              <Option value="active">Actif</Option>
              <Option value="expired">Expiré</Option>
              <Option value="terminated">Terminé</Option>
              <Option value="suspended">Suspendu</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Employé"
              value={employeeFilter}
              onChange={setEmployeeFilter}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">Tous les employés</Option>
              {employees.map((employee) => (
                <Option key={employee.id} value={employee.id.toString()}>
                  {employee.first_name} {employee.last_name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Type de contrat"
              value={contractTypeFilter}
              onChange={setContractTypeFilter}
              style={{ width: "100%" }}
            >
              <Option value="all">Tous les types</Option>
              {contractTypes.map((type) => (
                <Option key={type.id} value={type.id.toString()}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker
              placeholder={["Date début min", "Date début max"]}
              value={dateRange}
              onChange={setDateRange}
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
            />
          </Col>
        </Row>
      </Card>

      {/* Table des contrats */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredContracts}
          rowKey="id"
          loading={loading}
          pagination={{
            total: filteredContracts.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} contrats`,
          }}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>

      {/* Modal du formulaire */}
      <ContractForm
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingContract(null);
        }}
        onSuccess={handleModalSuccess}
        contract={editingContract}
        employees={employees}
        contractTypes={contractTypes}
      />
    </div>
  );
};

export default ContractList;
